import React from 'react';
import cx from 'classnames';
import { Text, Input, Link, Divider } from '../../../ui-kit';

import '../../PaymentSummary/styles/_payment-summary-content.scss';

const OUTSTANDING_NOTE =
'Outstanding amounts will be billed to your organisation at the end of the month.';

// A standard "label — value" row in the breakdown (Production, Add-ons, etc.).
const LineItem = ({ label, value }) =>
<div className="payment-summary-content--credits-total__section--row">
    <div className="payment-summary-content--credits-total__section--row__item">{label}</div>
    <div className="payment-summary-content--credits-total__section--row__item">{value}</div>
  </div>;

// An emphasised "label — value" row (Subtotal, Amount Due).
const HighlightRow = ({ label, value }) =>
<div className="payment-summary-content--credits-total__section--row">
    <div className="payment-summary-content--credits-total__highlight">{label}</div>
    <div className="payment-summary-content--credits-total__highlight">{value}</div>
  </div>;

// Single, canonical "Project Cost Summary" panel — the right-hand summary column
// of the shared PaymentSummary modal, extracted so every surface (the modal body
// PaymentSummaryContentV2 and the internal-staff Costs tab) renders one identical
// breakdown instead of bespoke copies.
// Presentational only: callers pass already-formatted credit/dollar strings.
// `showPaidBadge` (the "Paid" chip in the header) and `payment.hasPaid` (the
// Total / Amount Paid / Amount Due block) are deliberately separate: the modal
// surfaces the paid totals, the inline Costs tab surfaces the badge — each
// renders a different element, so callers opt into only what their surface shows.
// `onEditCosts` (optional) renders an internal-only "Edit Costs" button that
// links out to the dedicated costs interface — passed only by the modal body for
// internal users, never by the Costs tab itself (which is that destination).
const ProjectCostSummary = ({
  videoReadableId,
  productionCredits,
  addonsExtrasCost,
  creativeServicesCredits,
  subtotal,
  onDownload,
  showPaidBadge = false,
  billing = {},
  payment = {},
  onEditCosts
}) => {
  const {
    purchaseOrderId,
    onPurchaseOrderChange,
    onPurchaseOrderUpdate,
    canUpdatePurchaseOrder = false
  } = billing;
  const {
    hasPaid = false,
    total,
    amountPaid,
    amountDue,
    canProcessPayment = false
  } = payment;

  return (
    <div className="payment-summary-content--credits-total">
      <div className="payment-summary-content--credits-total__section">
        <div className="payment-summary-content--credits-total__section--row">
          <div className="payment-summary-content--credits-total__heading">Summary</div>
          <div className="payment-summary-content--credits-total__actions">
            {onDownload &&
            <Link
              onClick={onDownload}
              className="payment-summary-content--credits-total__link">

                Download
              </Link>}

            {onEditCosts &&
            <Link
              onClick={onEditCosts}
              className="payment-summary-content--credits-total__link">

                Edit Costs
              </Link>}

            {showPaidBadge &&
            <div className="payment-summary-content__payment-status">
                <span className="payment-summary-content__payment-status--completed">
                  Paid
                </span>
              </div>}

          </div>
        </div>

        <div className="payment-summary-content--credits-total__section--line-items">
          <div className="payment-summary-content--credits-total__highlight">
            {videoReadableId}
          </div>

          <LineItem label="Production" value={productionCredits} />
          <LineItem label="Add-ons & Extras" value={addonsExtrasCost} />
          <LineItem label="Creative Services" value={creativeServicesCredits} />
        </div>

        <Divider />

        <HighlightRow label="Subtotal" value={subtotal} />

        <Divider />
      </div>

      <Input
        label="Billing Instructions"
        defaultValue={purchaseOrderId}
        value={purchaseOrderId}
        onChange={onPurchaseOrderChange}
        labelProps={{
          extraLabelContent:
          <Link
            className={cx({
              'payment-summary-content--credits-total__link': canUpdatePurchaseOrder
            })}
            onClick={onPurchaseOrderUpdate}>

              Update
            </Link>

        }} />


      {
      hasPaid &&
      <div className="payment-summary-content--credits-total__section">
            <div className="payment-summary-content--credits-total__section--row">
              <div className="payment-summary-content--credits-total__heading">Total</div>
              <div className="payment-summary-content--credits-total__heading">
                {total}
              </div>
            </div>

            <LineItem label="Amount Paid" value={amountPaid} />

            <Divider />

            <HighlightRow label="Amount Due" value={amountDue} />

            <Divider />

            <div className="payment-section__footer-start-adornment-text">
              {OUTSTANDING_NOTE}
            </div>
          </div>}


      {
      canProcessPayment &&
      <>
            <div className="payment-summary-content__credits-total__summary--amount-due-footer">
              <Text
            heading5
            noMargin
            className="payment-summary-content__summary">

                Amount Due
              </Text>
              <Text
            heading5
            noMargin
            className="payment-summary-content__summary">

                {total}
              </Text>
            </div>
            <Text caption>{OUTSTANDING_NOTE}</Text>
          </>}


    </div>);

};


export default ProjectCostSummary;
